import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Nav, Cover, Footer } from '../components/Chrome';
import { useGetApp } from '../components/GetTheApp';
import {
  entryLabel,
  getEntries,
  getSeries,
  getUserById,
  isEntryLockedOnWeb,
  readAccessOf,
} from '../firebase/reads';
import { stripImageTokens } from '../entryContent';
import { usePageMeta } from '../usePageMeta';

const ACCESS_NOTE = {
  friends: 'Only the author’s friends can read past the first entry.',
  subscribers: 'Only subscribers can read past the first entry — subscribing is free.',
};

/** longreads.me/read/<seriesId> — a book's cover page and its table of contents. */
function Book() {
  const { seriesId } = useParams();
  const getApp = useGetApp();

  const [state, setState] = useState({
    status: 'loading',
    series: null,
    entries: [],
    author: null,
  });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', series: null, entries: [], author: null });

    (async () => {
      try {
        const series = await getSeries(seriesId);
        if (cancelled) return;
        if (!series) {
          setState({ status: 'missing', series: null, entries: [], author: null });
          return;
        }
        const [entries, author] = await Promise.all([
          getEntries(seriesId),
          getUserById(series.authorId),
        ]);
        if (cancelled) return;
        setState({ status: 'ready', series, entries, author });
      } catch {
        if (!cancelled) setState({ status: 'error', series: null, entries: [], author: null });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [seriesId]);

  const { status, series, entries, author } = state;
  const accessNote = series ? ACCESS_NOTE[readAccessOf(series)] : null;

  usePageMeta(
    series ? `${series.title} · Long reads` : 'Long reads',
    series?.description || 'Read it on Long reads.'
  );

  return (
    <div className="app">
      <Nav />
      <main className="page">
        {status === 'loading' && <p className="state">Loading…</p>}
        {status === 'error' && <p className="state">Could not load this long read.</p>}
        {status === 'missing' && (
          <p className="state">This long read isn’t available.</p>
        )}

        {status === 'ready' && (
          <>
            <header className="book-head">
              <Cover series={series} className="book-cover" />
              <div>
                <h1 className="book-title">{series.title}</h1>
                <p className="byline">
                  by{' '}
                  {author?.username ? (
                    <Link to={`/${author.username}`}>@{author.username}</Link>
                  ) : (
                    'unknown'
                  )}
                </p>
                <p className="meta">
                  {series.entryCount || 0} {series.entryCount === 1 ? 'entry' : 'entries'} ·{' '}
                  {series.subscriberCount || 0}{' '}
                  {series.subscriberCount === 1 ? 'subscriber' : 'subscribers'}
                </p>
              </div>
            </header>

            {series.description && <p className="book-desc">{series.description}</p>}

            <div className="rail">
              <button className="btn btn-primary" onClick={() => getApp('subscribe to this')}>
                Subscribe
              </button>
              <button className="btn" onClick={() => getApp('save this to your library')}>
                Save
              </button>
            </div>

            <h2 className="section-label">ENTRIES</h2>
            {accessNote && <p className="series-sub" style={{ marginBottom: 12 }}>{accessNote}</p>}

            {entries.length === 0 ? (
              <p className="state">Nothing published yet.</p>
            ) : (
              entries.map((entry) => {
                const locked = isEntryLockedOnWeb(series, entry);
                return (
                  <Link
                    key={entry.id}
                    className="entry-row"
                    to={`/read/${seriesId}/${entry.id}`}
                  >
                    <div style={{ flex: 1 }}>
                      <span className="entry-label">{entryLabel(entry)}</span>
                      {entry.title && <h3 className="entry-title">{entry.title}</h3>}
                      {!locked && entry.snippet && (
                        <p className="entry-snippet">
                          {stripImageTokens(entry.snippet).slice(0, 140)}
                        </p>
                      )}
                    </div>
                    {locked && (
                      <span className="lock" role="img" aria-label="Locked">
                        🔒
                      </span>
                    )}
                  </Link>
                );
              })
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default Book;
